import React, {useEffect, useLayoutEffect, useRef, useState, ReactNode} from "react";
import type Slider from "react-slick";
import { SlickNextArrow, SlickPrevArrow } from "../content/MultiMediaSlider";
import { TeaserSliderWrapper } from "./TeaserWidgetReactSlickLazy";
import { useTeaserWrapperClassNames } from "./TeaserWrapper";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";



interface TeaserWidgetSliderProps {
  highlightCount: number
  children: ReactNode
}

const AUTOPLAY_DWELL_MS = 5000;
const AUTOPLAY_TRANSITION_MS = 500;
const AUTOPLAY_EASE = "cubic-bezier(0.65, 0, 0.35, 1)";
// Non-zero dwell is clamped to this floor: a unit mistake (45 for 4500) must not strobe.
const AUTOPLAY_MIN_DWELL_MS = 1000;
const AUTOPLAY_MIN_TRANSITION_MS = 100;

interface AutoplayTiming {
  dwell: number
  speed: number
  ease: string
}

const DEFAULT_AUTOPLAY_TIMING: AutoplayTiming = {
  dwell: AUTOPLAY_DWELL_MS,
  speed: AUTOPLAY_TRANSITION_MS,
  ease: AUTOPLAY_EASE,
};

// Unlike readIntVar below this keeps 0/negative (explicit "autoplay off"). The whole value must
// be numeric and unitless ("800ms" is unparsable, not 800); unset/unparsable → undefined so the
// default applies. Fractional values round instead of truncating (0.5 must not become "off").
function readMsVar(el: HTMLElement, name: string) {
  const v = getComputedStyle(el).getPropertyValue(name).trim();
  if (!v) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : undefined;
}

function readAutoplayTiming(el: HTMLElement): AutoplayTiming {
  const rawDwell = readMsVar(el, '--t4-tw-autoplay-dwell') ?? AUTOPLAY_DWELL_MS;
  const dwell = rawDwell <= 0 ? 0 : Math.max(rawDwell, AUTOPLAY_MIN_DWELL_MS);
  const rawSpeed = readMsVar(el, '--t4-tw-autoplay-speed') ?? AUTOPLAY_TRANSITION_MS;
  const speed = Math.min(Math.max(rawSpeed, AUTOPLAY_MIN_TRANSITION_MS), dwell || AUTOPLAY_DWELL_MS);
  const ease = getComputedStyle(el).getPropertyValue('--t4-tw-autoplay-ease').trim() || AUTOPLAY_EASE;
  return {dwell, speed, ease};
}

export function TeaserWidgetSlider({highlightCount, children}: TeaserWidgetSliderProps) {
  // The page position is derived from this index and the current slide count rather than being
  // set in beforeChange: a resize can turn the current page into the last page without
  // react-slick ever firing beforeChange, which would otherwise stall the autoplay wrap.
  const [currentIndex, setCurrentIndex] = useState(0);
  const hostRef = useRef<HTMLDivElement|null>(null);
  const [showAndScroll, setShowAndScroll] = useState<{show:number, scroll:number}>({show: 1, scroll: 1});
  // False on the server and during hydration: slide counts come from CSS, which only the client
  // can measure. Until then the slider must keep the plain settings, otherwise SSR emits
  // infinite-mode clone slides for teasers that resolve to a single page.
  const [cssMeasured, setCssMeasured] = useState(false);
  const [autoplayTiming, setAutoplayTiming] = useState<AutoplayTiming>(DEFAULT_AUTOPLAY_TIMING);
  // see LiveblogHighlightSliderSummary.tsx for why this is keyed off context rather than
  // only the ResizeObserver below.
  const {breakpoint, layout} = useTeaserWrapperClassNames();
  const sliderRef = useRef<Slider|null>(null);
  const [userInteracted, setUserInteracted] = useState(false);
  // Hover and keyboard focus are independent engagement conditions: autoplay pauses while
  // either is active and resumes only when both are gone.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const engaged = hovered || focused;

  const readIntVar = (el: HTMLElement, name: string) => {
    const v = getComputedStyle(el).getPropertyValue(name).trim();
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }


  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    const applyFromCss = () => {
      const slides = readIntVar(el, '--t4-tw-slides') ?? 1;
      const scroll = readIntVar(el, '--t4-tw-scroll') ?? slides;
      const clampedSlides = highlightCount ? Math.min(slides, highlightCount) : slides;
      const timing = readAutoplayTiming(el);
      setCssMeasured(true);
      setAutoplayTiming(prev => (prev.dwell !== timing.dwell || prev.speed !== timing.speed || prev.ease !== timing.ease)
        ? timing
        : prev
      );
      setShowAndScroll(prev => (prev.show !== clampedSlides || prev.scroll !== scroll)
        ? { show: clampedSlides, scroll }
        : prev
      );
    };

    const ro = new ResizeObserver(() => {
      applyFromCss();
    });
    ro.observe(el);

    // Initial reads with fallbacks in case external CSS loads slightly later
    applyFromCss();
    const rafId = window.requestAnimationFrame(() => applyFromCss());
    const timeoutId = window.setTimeout(() => applyFromCss(), 120);
    const onLoad = () => applyFromCss();
    window.addEventListener('load', onLoad, { once: true });
    // Viewport media queries can change the CSS variables without the element resizing
    // (fixed-width container), which neither the ResizeObserver nor the breakpoint sees.
    const onWindowResize = () => applyFromCss();
    window.addEventListener('resize', onWindowResize);

    return () => {
      ro.disconnect();
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('load', onLoad as any);
      window.removeEventListener('resize', onWindowResize);
    };
  }, [highlightCount, breakpoint]);

  useEffect(() => {
    const el = hostRef.current;
    if (!el || layout !== 'slider-autoplay') return;
    const onInteraction = () => {
      sliderRef.current?.slickPause();
      setUserInteracted(true);
    };
    // react-slick's own pauseOnHover only covers .slick-list — arrows and dots sit outside it,
    // so the host pauses slick itself; the engagement-guarded restart effect resumes on leave.
    // focusin/focusout give keyboard users the same pause a hovering pointer gets.
    const pause = () => sliderRef.current?.slickPause();
    const onEnter = () => { setHovered(true); pause(); };
    const onLeave = () => setHovered(false);
    const onFocusIn = () => { setFocused(true); pause(); };
    const onFocusOut = (e: FocusEvent) => {
      if (!(e.relatedTarget instanceof Node) || !el.contains(e.relatedTarget)) setFocused(false);
    };
    // pointerdown covers swipes; click covers keyboard activation (Enter/Space) of arrows and dots
    el.addEventListener('pointerdown', onInteraction);
    el.addEventListener('click', onInteraction);
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    el.addEventListener('focusin', onFocusIn);
    el.addEventListener('focusout', onFocusOut);
    return () => {
      el.removeEventListener('pointerdown', onInteraction);
      el.removeEventListener('click', onInteraction);
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
      el.removeEventListener('focusin', onFocusIn);
      el.removeEventListener('focusout', onFocusOut);
    };
  }, [layout]);

  const slidesToShow = showAndScroll.show;
  const slidesToScroll = showAndScroll.scroll ?? showAndScroll.show;
  const slideActive = highlightCount && slidesToShow < highlightCount;
  const possibleLastIndex =  Math.max(0, (highlightCount || 0) - slidesToShow);
  const firstSlide = currentIndex === 0;
  const lastSlides = currentIndex >= possibleLastIndex;
  // Deliberately no react-slick `infinite` mode: its clone slides break the boundary-margin
  // CSS (first/last-child rules), the first/last classes, and SSR markup. The wrap-around is
  // a timed rewind to the first page instead (effect below).
  const autoplayEnabled = layout === 'slider-autoplay' && cssMeasured && autoplayTiming.dwell > 0 && !!slideActive && !prefersReducedMotion;
  const autoplayActive = autoplayEnabled && !userInteracted;

  // react-slick ignores an `autoplay` prop that turns true after mount while it is in its
  // "paused" state (which a false prop puts it into), so a resize that crosses the one-page
  // threshold has to (re)start the timer explicitly.
  // Guarded by engagement: a breakpoint or media-query change while the pointer rests on (or
  // focus is inside) the widget must not restart autoplay under the user; the restart happens
  // on leave instead (slickPlay also resets the interval phase → a full dwell after the pause).
  useEffect(() => {
    if (autoplayActive && !engaged) sliderRef.current?.slickPlay();
  }, [autoplayActive, autoplayTiming.dwell, engaged]);

  // react-slick's own autoplay cannot advance past the last page without `infinite`, so the
  // wrap-around is ours: dwell on the last page, then rewind to the first. slickPlay() resets
  // the internal interval phase so the first page gets a full dwell again.
  useEffect(() => {
    if (!autoplayActive || !lastSlides || engaged) return;
    const id = window.setTimeout(() => {
      sliderRef.current?.slickGoTo(0);
      sliderRef.current?.slickPlay();
    }, autoplayTiming.dwell);
    return () => window.clearTimeout(id);
  }, [autoplayActive, lastSlides, engaged, autoplayTiming.dwell]);

  if (!highlightCount) {
    return <></>;
  }


  const motionSettings = autoplayEnabled ? {
    infinite: false,
    autoplay: autoplayActive,
    autoplaySpeed: autoplayTiming.dwell,
    speed: autoplayTiming.speed,
    cssEase: autoplayTiming.ease,
    pauseOnHover: true,
  } : {
    infinite: false,
    speed: 150,
  };

  const settings = {
    ...motionSettings,
    adaptiveHeight: false,
    slidesToShow: slidesToShow,
    slidesToScroll: slidesToScroll,
    className: `
      tik4-sl tik4-tws__sl
      ${firstSlide ? 'tik4-tws__sl--first-slide' : ''}
      ${lastSlides ? 'tik4-tws__sl--last-slides' : ''}
      ${slideActive ? 'tik4-tws__sl--slide-active' : ''}
      `,
    nextArrow: <SlickNextArrow />,
    prevArrow: <SlickPrevArrow />,
    dots: true,
    appendDots: (dots:React.ReactNode) => <ul className="tik4-sl__d">{dots}</ul>,
        dotsClass: "tik4-sl__d",
    customPaging: (i:number) => (
      <button className="tik4-sl__d__d">
        <div className="tik4-sl__d__d__i">{i+1}</div>
      </button>
    ),
    beforeChange: (current:any, next:any) => {
      setCurrentIndex(next);
    }
  }

  return <React.Fragment>
    {highlightCount && highlightCount > 0 &&
        <div
          className="tik4-tws"
          ref={hostRef}
        >
          <TeaserSliderWrapper ref={sliderRef} {...settings} >
            {children}
          </TeaserSliderWrapper>
        </div>
      }
  </React.Fragment>
}
